// api.js — fetch wrapper with automatic token refresh on 401.
import { ref } from "vue"

const ACCESS_KEY  = "nsp_access_token"
const REFRESH_KEY = "nsp_refresh_token"

function _get(key) {
  try { return localStorage.getItem(key) || "" } catch { return "" }
}

// Listeners notified after a successful refresh (token response payload) or a
// failed one (null).  store.js subscribes to keep its reactive refs in sync —
// a plain callback registry avoids a circular import (store.js imports api.js).
const _tokenListeners = []
function onTokensChanged(fn) { _tokenListeners.push(fn) }
function _notify(payload) {
  for (const fn of _tokenListeners) {
    try { fn(payload) } catch { /* listener errors must not break requests */ }
  }
}

// Single-flight refresh: when several requests hit 401 simultaneously they all
// await the same promise instead of racing each other with refresh calls.
// The slot is cleared in a .finally() chained *after* the assignment: clearing it
// inside the async body ran too early whenever it returned before its first
// await (no refresh token), and left a settled promise stuck here forever.
let _refreshPromise = null

function _refreshTokens() {
  if (!_refreshPromise) {
    _refreshPromise = _doRefresh().finally(() => { _refreshPromise = null })
  }
  return _refreshPromise
}

function _dropSession() {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
  _notify(null)   // store.js → onSessionExpired → redirect to login
}

async function _doRefresh() {
  const refresh = _get(REFRESH_KEY)
  if (!refresh) { _dropSession(); return false }   // access token rejected, nothing to renew it with
  try {
    const r = await fetch("/api/auth/token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ grant_type: "refresh_token", refresh_token: refresh }),
    })
    const data = await r.json().catch(() => null)
    if (!r.ok || !data?.access_token) throw new Error("refresh failed")
    localStorage.setItem(ACCESS_KEY, data.access_token)
    if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token)
    _notify(data)
    return true
  } catch {
    // Refresh token is gone/expired/revoked — drop the session cleanly.
    _dropSession()
    return false
  }
}

/** Incremented after every successful write, so a kept-alive view can tell its data may be stale. */
const writeCount = ref(0)

const API = {
  async req(method, url, body, _canRetry = true) {
    const headers = { "Content-Type": "application/json" }
    const token = _get(ACCESS_KEY)
    if (token) headers["Authorization"] = "Bearer " + token
    const opts = { method, headers, credentials: "same-origin" }
    if (body !== undefined) opts.body = JSON.stringify(body)
    const r = await fetch(url, opts)

    // Expired access token → refresh once and replay the request.
    // Only when the request actually attempted Bearer auth, and never for the
    // token endpoint itself (its 401s mean bad credentials, not expiry).
    if (r.status === 401 && token && _canRetry && url !== "/api/auth/token") {
      if (await _refreshTokens()) {
        return this.req(method, url, body, false)
      }
    }

    const data = await r.json().catch(() => null)
    if (!r.ok) {
      // detail is a message; FastAPI's own 422s make it a list, and older routes
      // nested {errors} inside it. Never let an object become "[object Object]".
      const detail = data?.detail
      const err = new Error(typeof detail === "string" && detail ? detail : (r.statusText || "Ошибка " + r.status))
      err.status = r.status
      err.errors = data?.errors ?? (detail && typeof detail === "object" && !Array.isArray(detail) ? detail.errors : null) ?? null
      throw err
    }
    if (method !== "GET") writeCount.value++   // cached views (the Dashboard) know to refetch
    return data
  },
  get(url)        { return this.req("GET", url) },
  post(url, body) { return this.req("POST", url, body) },
  patch(url, body){ return this.req("PATCH", url, body) },
  put(url, body)  { return this.req("PUT", url, body) },
  del(url)        { return this.req("DELETE", url) },
}

export { API, onTokensChanged, writeCount }